import { throwError as observableThrowError, Observable, Subject } from 'rxjs';

import { map, catchError, takeUntil } from 'rxjs/operators';
import { Injectable, Injector, OnDestroy } from '@angular/core';
import {
  HttpClient,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';
import { LocalStorageService } from 'ngx-webstorage';

import { environment } from 'environments/environment';
import { Role, AdministrationRole } from 'api/enums';
import {
  UserInfoDto,
  UserDepartmentDto,
  UserCompanyDto,
  UserRolesDto
} from 'api/models';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage } from '@azure/msal-browser';

@Injectable({ providedIn: 'root' })
export class AuthenticationService implements OnDestroy {
  public static SKIP_INTERCEPTOR = 'X-Skip-Auth-Interceptor';
  private _destroyed = new Subject();
  private _accessToken: string;
  private _roles: UserRolesDto | null;

  private get roles(): Role {
    return this._roles?.roles || Role.None;
  }

  private get adminRoles(): AdministrationRole {
    return this._roles?.adminRoles || AdministrationRole.None;
  }

  private get msal(): MsalService {
    return this.injector.get(MsalService);
  }

  private get localStorage(): LocalStorageService {
    return this.injector.get(LocalStorageService);
  }

  public get accessToken(): string {
    return this._accessToken;
  }


  constructor(
    private http: HttpClient,
    private injector: Injector
  ) {
    this.injector
      .get(MsalBroadcastService)
      .msalSubject$.pipe(takeUntil(this._destroyed))
      .subscribe(result => this.handleMsalBroadcast(result));
  }

  private async handleMsalBroadcast(result: EventMessage) {
    switch (result.eventType) {
      case 'msal:acquireTokenFailure':
        this._accessToken = null;
        break;
      case 'msal:acquireTokenSuccess':
        this._accessToken = (result.payload as AuthenticationResult).accessToken;
        await this.setRoles();
        break;
    }
  }

  private async setRoles() {
    this._roles = await this.getRoles();
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }

  private async getRoles(): Promise<UserRolesDto> {
    return await this.http
      .get<UserRolesDto>(`${environment.apiHost}api/account/roles`)
      .pipe(catchError(err => this.handleError(err)))
      .toPromise();
  }

  private get account(): AccountInfo | null {
    const accounts = this.msal.instance.getAllAccounts();
    return accounts ? accounts[0] : null;
  }

  async logOut(): Promise<void> {
    this._roles = null;
    this.localStorage.clear('activeCompany');
    this.localStorage.clear('activeDepartment');
    await this.msal
      .logout({
        account: this.account,
        logoutHint: this.account?.idTokenClaims?.login_hint,
        postLogoutRedirectUri: null
      })
      .toPromise();
  }

  getUserType(): Role | null {
    return this.roles > 0 ? this.roles : null;
  }

  hasAnyRole(roles: Role, adminRoles: AdministrationRole): boolean {
    // tslint:disable-next-line: no-bitwise
    return (this.roles & roles) > 0 || (this.adminRoles & adminRoles) > 0;
  }

  getUserInfo(): Observable<UserInfoDto> {
    return this.http
      .get<UserInfoDto>(`${environment.apiHost}api/account/userinfo`)
      .pipe(
        map(json => {
          const userInfo = new UserInfoDto(json, this.roles, this.adminRoles);

          if (json.departments != null) {
            userInfo.departments = [];
            for (const department of json.departments)
              userInfo.departments.push(new UserDepartmentDto(department));
          }

          if (json.mainDepartment != null)
            userInfo.mainDepartment = new UserDepartmentDto(
              json.mainDepartment
            );

          if (json.companies != null) {
            userInfo.companies = new Array<UserCompanyDto>();
            for (const company of json.companies) {
              userInfo.companies.push(new UserCompanyDto(company));
            }
          }

          if (json.caseLengths) userInfo.caseLengths = json.caseLengths;

          if (json.caseDifficulties)
            userInfo.caseDifficulties = json.caseDifficulties;

          return userInfo;
        }),
        catchError(this.handleError)
      );
  }

  private handleError(error: HttpResponse<any> | any) {
    let errMsg: string;
    if (error instanceof HttpResponse && error != null) {
      const body = error.body || '';
      if (body.error === 'invalid_grant') {
        errMsg = body.error_description;
      } else {
        const err = body.error || JSON.stringify(body);
        errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
      }
    } else if (error instanceof HttpErrorResponse && error != null) {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return observableThrowError(errMsg);
  }
}
